import { LocalStorageKeys } from './types';
import { getCookie, setCrossDomainCookie } from './cookies';

export const getEffectiveKey = (key: string, useCrossDomain: boolean, userId?: number): string => (
    useCrossDomain && userId ? `${key}_user_${userId}` : key
);

export const clearLocalStorage = (key: LocalStorageKeys) => {
    try {
        window.localStorage.removeItem(key);
    } catch (error) {
        console.error(`Error clearing localStorage key "${key}":`, error);
    }
};

export const storeLocalStorage = <T = unknown>(key: LocalStorageKeys, value: T, useCrossDomain = false, userId?: number): void => {
    try {
        const effectiveKey = getEffectiveKey(key, useCrossDomain, userId);
        const stringifiedValue = JSON.stringify(value);
        window.localStorage.setItem(effectiveKey, stringifiedValue);

        if (useCrossDomain) {
            setCrossDomainCookie(effectiveKey, stringifiedValue);
        }
    } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
    }
};

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

const dateReviver = (_key: string, value: unknown): unknown => {
    if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
        return new Date(value);
    }
    return value;
};

export const getLocalStorage = <T = unknown>(key: LocalStorageKeys, useCrossDomain = false, userId?: number): T | null => {
    try {
        const effectiveKey = getEffectiveKey(key, useCrossDomain, userId);
        let value: string | null = null;

        if (useCrossDomain) {
            const cookieValue = getCookie(effectiveKey);
            if (cookieValue) {
                value = cookieValue;
                window.localStorage.setItem(effectiveKey, cookieValue);
            }
        }

        if (!value) {
            value = window.localStorage.getItem(effectiveKey);
        }

        return value ? (JSON.parse(value, dateReviver) as T) : null;
    } catch (error) {
        console.error(`Error getting localStorage key "${key}":`, error);
        return null;
    }
};
